import type {
  HTMLAttributes,
  TableHTMLAttributes,
  ThHTMLAttributes,
  TdHTMLAttributes,
} from "react";
import { cn } from "@/lib/cn";

function Root({ className, ...props }: TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div className="relative w-full overflow-auto">
      <table
        className={cn("w-full caption-bottom border-separate border-spacing-0 text-sm", className)}
        {...props}
      />
    </div>
  );
}

function Header({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      className={cn(
        "sticky top-0 z-[1] bg-subtle text-fg-muted [&_th]:border-b [&_th]:border-border-subtle",
        className,
      )}
      {...props}
    />
  );
}

function Body({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn("[&_tr:last-child_td]:border-b-0", className)} {...props} />;
}

interface RowProps extends HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean;
}
function Row({ className, selected, ...props }: RowProps) {
  return (
    <tr
      data-selected={selected || undefined}
      className={cn(
        "transition-colors duration-[var(--duration-micro)] hover:bg-surface-hover",
        "data-[selected=true]:bg-accent-subtle/40",
        className,
      )}
      {...props}
    />
  );
}

function Head({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        "h-9 px-3 text-left text-[11px] font-medium uppercase tracking-wider text-fg-subtle",
        "[&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
}

function Cell({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={cn(
        "h-11 px-3 align-middle border-b border-border-subtle text-fg",
        "[&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
}

export const Table = Object.assign(Root, {
  Header,
  Body,
  Row,
  Head,
  Cell,
});
